/**
 * Analytics Middleware
 *
 * Automatically captures core metric events by intercepting Redux actions.
 * Uses createListenerMiddleware (same pattern as mealPlanSyncMiddleware).
 */

import { createListenerMiddleware } from '@reduxjs/toolkit';
import { trackEvent } from '../services/analyticsService';
import { addFeedbackEvent } from './feedbackSlice';
import { getCurrentMondayStart } from './mealPlanSlice';

export const analyticsMiddleware = createListenerMiddleware();

// --- Auth funnel ---

analyticsMiddleware.startListening({
  type: 'auth/registerUser/fulfilled',
  effect: (action: any) => {
    trackEvent('user_signed_up', { method: 'email' });
  },
});

// One Google button serves both "create account" and "sign in", so the event
// has to come from Firebase's isNewUser rather than the button that was
// pressed. Reporting every returning sign-in as a signup inflated the metric
// and fired the new-signup Slack alert for people who already had accounts.
analyticsMiddleware.startListening({
  type: 'auth/loginWithGoogle/fulfilled',
  effect: (action: any) => {
    if (action.payload?.isNewUser) {
      trackEvent('user_signed_up', { method: 'google' });
    } else {
      trackEvent('user_logged_in', { method: 'google' });
    }
  },
});

analyticsMiddleware.startListening({
  type: 'auth/loginUser/fulfilled',
  effect: (action: any) => {
    trackEvent('user_logged_in', {
      method: 'email',
      email: action.payload?.email,
    });
  },
});

analyticsMiddleware.startListening({
  type: 'auth/continueAsGuest',
  effect: () => {
    trackEvent('user_continued_as_guest');
  },
});

analyticsMiddleware.startListening({
  type: 'auth/logout/fulfilled',
  effect: () => {
    trackEvent('user_logged_out');
  },
});

// --- Meal plan activation ---

analyticsMiddleware.startListening({
  type: 'mealPlan/addToWeek',
  effect: (action: any) => {
    trackEvent('meal_added_to_week', { source: 'recipe' });
  },
});

analyticsMiddleware.startListening({
  type: 'mealPlan/addFreeTextMeal',
  effect: (action: any) => {
    trackEvent('meal_added_to_week', { source: 'free_text' });
  },
});

/**
 * One generation, one event — even when it lands in two week buckets.
 *
 * The horizon runs seven days from today, so a plan built mid-week spills past
 * Sunday, and the caller dispatches `setWeeklyPlan` once per bucket:
 * `mealPlanSyncMiddleware` resolves a single week from `payload.weekStartDate`,
 * so one dispatch would leave the spilled days in Redux and out of Firestore.
 *
 * Reporting each dispatch would split one seven-meal plan into a three and a
 * four — `week_plan_generated.meal_count` is the metric this whole change is
 * measured by, so it has to describe the generation, not the write.
 */
let pendingPlanMeals = 0;
let pendingPlanSource: string | null = null;
let pendingPlanFlush: ReturnType<typeof setTimeout> | null = null;

analyticsMiddleware.startListening({
  type: 'mealPlan/setWeeklyPlan',
  effect: (action: any) => {
    pendingPlanMeals += action.payload?.addedCount ?? action.payload?.meals?.length ?? 0;
    // Every bucket of one generation carries the same `source`, so the first
    // dispatch of the batch names it. 'unknown' rather than omitted: a missing
    // property is indistinguishable from an old client in PostHog, and this is
    // the property that separates a plan the cook asked for from one the app
    // decided on by itself.
    pendingPlanSource = pendingPlanSource ?? action.payload?.source ?? 'unknown';
    if (pendingPlanFlush) return;
    pendingPlanFlush = setTimeout(() => {
      const meal_count = pendingPlanMeals;
      const source = pendingPlanSource ?? 'unknown';
      pendingPlanMeals = 0;
      pendingPlanSource = null;
      pendingPlanFlush = null;
      trackEvent('week_plan_generated', { meal_count, source });
    }, 0);
  },
});

// --- Week navigation ---

// Browsing weeks should be free. This event exists to prove it: `offset_weeks`
// is the distance from the real current week, so a regression that reintroduces
// work on navigation can be correlated against how far the user jumped.
analyticsMiddleware.startListening({
  type: 'mealPlan/setCurrentWeek',
  effect: (action: any) => {
    const target = action.payload;
    if (typeof target !== 'string') return;
    const currentWeek = getCurrentMondayStart();
    const offsetWeeks = Math.round(
      (new Date(target + 'T12:00:00').getTime() -
        new Date(currentWeek + 'T12:00:00').getTime()) /
        (7 * 24 * 60 * 60 * 1000)
    );
    trackEvent('week_changed', { offset_weeks: offsetWeeks });
  },
});

// Note: `grocery_list_generated` is emitted by the `buildGroceryList` thunk
// rather than here, so it can carry the measured generation `duration_ms`.

analyticsMiddleware.startListening({
  type: 'groceryList/toggleItem',
  effect: () => {
    trackEvent('grocery_item_toggled');
  },
});

analyticsMiddleware.startListening({
  type: 'groceryList/addCustomItem',
  effect: () => {
    trackEvent('grocery_custom_item_added');
  },
});

// --- Pantry usage ---

analyticsMiddleware.startListening({
  type: 'inventory/addItemsAndSync/fulfilled',
  effect: (action: any) => {
    const itemCount = Array.isArray(action.payload) ? action.payload.length : 1;
    trackEvent('pantry_items_added', { item_count: itemCount });
  },
});

analyticsMiddleware.startListening({
  type: 'inventory/addItemsToPantryAndSync/fulfilled',
  effect: (action: any) => {
    const itemCount = Array.isArray(action.payload) ? action.payload.length : 1;
    trackEvent('pantry_items_added', { item_count: itemCount });
  },
});

analyticsMiddleware.startListening({
  type: 'inventory/markAsEatenAndSync/fulfilled',
  effect: () => {
    trackEvent('pantry_item_eaten');
  },
});

// --- Onboarding funnel (Du Chef: three duels → allergies → a direction) ---

const AXIS_OF_QUESTION: Record<number, string> = { 1: 'base', 2: 'time', 3: 'heat' };

analyticsMiddleware.startListening({
  type: 'onboarding/answerQuestion',
  effect: (action: any, api) => {
    const p = action.payload ?? {};
    const variant = (api.getState() as any)?.onboarding?.variant ?? 0;
    trackEvent('onboarding_quiz_answered', {
      question: p.question,
      axis: AXIS_OF_QUESTION[p.question] ?? null,
      pair_id: p.pairId,
      card_a: p.cardA,
      card_b: p.cardB,
      winner: p.winner,
      variant,
    });
  },
});

analyticsMiddleware.startListening({
  type: 'onboarding/confirmSafety',
  effect: (action: any) => {
    const p = action.payload ?? {};
    trackEvent('onboarding_safety_confirmed', {
      allergen_count: p.none ? 0 : (p.keys?.length ?? 0),
      none: !!p.none,
      custom_count: p.none ? 0 : (p.custom?.length ?? 0),
    });
  },
});

analyticsMiddleware.startListening({
  type: 'onboarding/setGeneratedWeek',
  effect: (action: any) => {
    // The funnel, not just the outcome: `meal_count: 0` alone could not say
    // whether the library never loaded or the cook's own exclusions ate it.
    const d = action.payload?.diagnostics;
    // `people_changed: false` is a cook who kept the default. Whether they
    // saw the question at all is what #720 was about; this is how we tell.
    const people = action.payload?.people;
    trackEvent('onboarding_plan_generated', {
      meal_count: action.payload?.meals?.length ?? 0,
      ...(people ? { people: people.count, people_changed: people.changed } : {}),
      ...(d
        ? {
            recipes_considered: d.recipesConsidered,
            after_diet_filter: d.afterDietFilter,
            after_allergen_filter: d.afterAllergenFilter,
            allergen_terms_count: d.allergenTermsCount,
            relaxed_pass_used: d.relaxedPassUsed,
          }
        : {}),
    });
  },
});

/**
 * A first week that could not be built at all. Fires instead of
 * `onboarding_plan_generated`, so the two are never confused in a funnel.
 */
analyticsMiddleware.startListening({
  type: 'onboarding/recordEmptyPlan',
  effect: (action: any) => {
    const d = action.payload?.diagnostics;
    trackEvent('onboarding_plan_empty', {
      reason: action.payload?.reason,
      recipes_considered: d?.recipesConsidered ?? 0,
      after_diet_filter: d?.afterDietFilter ?? 0,
      after_allergen_filter: d?.afterAllergenFilter ?? 0,
      allergen_terms_count: d?.allergenTermsCount ?? 0,
    });
  },
});

analyticsMiddleware.startListening({
  type: 'onboarding/chooseDirection',
  effect: (action: any, api) => {
    const tonight = (api.getState() as any)?.onboarding?.tonightIdeas;
    trackEvent('onboarding_direction_chosen', {
      mode: action.payload,
      // #721: a tapped recipe versus a whole card, and how many recipes the card offered.
      via: action.meta?.via ?? 'card',
      tonight_count: Array.isArray(tonight) ? tonight.length : 0,
    });
  },
});

analyticsMiddleware.startListening({
  type: 'onboarding/recordAxisFlip',
  effect: (action: any) => {
    trackEvent('onboarding_plan_corrected', { kind: 'flip', axis: action.payload?.axis });
  },
});

analyticsMiddleware.startListening({
  type: 'onboarding/completeOnboarding',
  effect: (action: any) => {
    const p = action.payload;
    // No payload: the route guard completed onboarding for an existing user.
    trackEvent('onboarding_completed', p
      ? { mode: p.mode, duration_ms: p.durationMs, dinners: p.dinners, source: 'flow' }
      : { source: 'guard' });
  },
});

// --- Meal swap & feedback ---

analyticsMiddleware.startListening({
  actionCreator: addFeedbackEvent,
  effect: (action) => {
    const { action: fbAction, recipeId, recipeName, quickReason, detail } = action.payload;

    if (fbAction === 'swap') {
      trackEvent('meal_swapped', {
        recipe_id: recipeId,
        recipe_name: recipeName,
      });
    } else if (fbAction === 'regenerate') {
      trackEvent('meal_regenerated', {
        recipe_id: recipeId,
        recipe_name: recipeName,
        reason: quickReason,
        free_text: detail,
      });
    } else if (fbAction === 'remove') {
      trackEvent('meal_removed', {
        recipe_id: recipeId,
        recipe_name: recipeName,
        reason: quickReason,
        free_text: detail,
      });
    } else if (fbAction === 'replaced_with_freetext') {
      trackEvent('meal_replaced_with_freetext', {
        recipe_id: recipeId,
        recipe_name: recipeName,
        free_text: detail,
      });
    }
  },
});

// --- Household approval ---

// Household approval. `value: null` is a retraction, tracked so a noisy
// tap-untap pattern is visible rather than looking like low engagement.
analyticsMiddleware.startListening({
  type: 'mealPlan/setMealApproval',
  effect: (action: any) => {
    trackEvent('meal_approval_set', { value: action.payload?.value ?? 'retracted' });
  },
});

// Household cook assignment. `self` distinguishes claiming from volunteering
// someone else — a lopsided split is the thing worth seeing.
analyticsMiddleware.startListening({
  type: 'mealPlan/setMealCook',
  effect: (action: any, api: any) => {
    const uid = action.payload?.uid ?? null;
    const myUid = (api.getState() as any)?.auth?.user?.uid ?? null;
    trackEvent('meal_cook_assigned', {
      self: uid !== null && uid === myUid,
      trigger: 'manual',
    });
  },
});

// --- Household meal proposals ---

// A household member proposes a meal to an already-occupied or empty slot.
analyticsMiddleware.startListening({
  type: 'mealPlan/proposeMeal',
  effect: (action: any, api: any) => {
    const { recipe, scheduledFor, mealType } = action.payload;
    const state = api.getState() as any;
    const weekStart = state.mealPlan?.currentWeekStart;

    // Determine source: fan vs library
    const source = recipe.source === 'generated' ? 'library' : 'fan';

    // Count proposals in this slot to report after adding
    let slotProposalCount = 1;
    if (weekStart && state.mealPlan?.weeks?.[weekStart]) {
      const existingProposals = state.mealPlan.weeks[weekStart].filter(
        (m: any) => m.scheduledFor === scheduledFor && m.mealType === mealType && m.mode === 'proposed'
      ).length;
      slotProposalCount = existingProposals + 1;
    }

    trackEvent('meal_proposal_added', {
      source,
      slotProposalCount,
    });
  },
});

// A household member promotes a proposal to the menu, or removes it unanswered.
analyticsMiddleware.startListening({
  type: 'mealPlan/acceptMeal',
  effect: (action: any, api: any) => {
    const { mealId, uid } = action.payload;
    const state = api.getState() as any;

    // Find the meal that was accepted to check if it was a proposal
    let meal: any = null;
    if (state.mealPlan?.weeks) {
      for (const weekMeals of Object.values(state.mealPlan.weeks)) {
        const found = (weekMeals as any[]).find((m: any) => m.id === mealId);
        if (found) {
          meal = found;
          break;
        }
      }
    }

    if (!meal || meal.mode !== 'proposed') return;

    // Compute hoursOpen from createdAt to now
    const createdAt = meal.createdAt ? new Date(meal.createdAt).getTime() : Date.now();
    const now = Date.now();
    const hoursOpen = Math.round((now - createdAt) / (1000 * 60 * 60) * 10) / 10;

    trackEvent('meal_proposal_resolved', {
      keep: 'accepted',
      hoursOpen,
      proposalCount: 1,
    });
  },
});

// Remove a proposal from the slot without accepting it.
analyticsMiddleware.startListening({
  type: 'mealPlan/removeFromWeek',
  effect: (action: any, api: any) => {
    const mealId = action.payload;
    const state = api.getState() as any;
    const myUid = state.auth?.user?.uid ?? null;

    // Find the meal to check if it's a proposal
    let meal: any = null;
    if (state.mealPlan?.weeks) {
      for (const weekMeals of Object.values(state.mealPlan.weeks)) {
        const found = (weekMeals as any[]).find((m: any) => m.id === mealId);
        if (found) {
          meal = found;
          break;
        }
      }
    }

    if (!meal || meal.mode !== 'proposed') return;

    // Check if the remover is the author
    const byAuthor = meal.createdByUid === myUid;

    trackEvent('meal_proposal_removed', {
      byAuthor,
    });
  },
});
